
import { QuestionItem, AssessmentItem, QuestionItemType } from "@/types";
import {
  PropType,
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
} from "vue";
import { usePlugin } from "@/plugins";
import { groupBy, sum } from "ramda";

export default defineComponent({
  name: "QuestionPanel",
  props: {
    questionItems: {
      required: true,
      type: Array as PropType<Array<QuestionItem>>,
    },
    assessmentItems: {
      type: Array as PropType<Array<AssessmentItem>>,
    },
    supervisorAssessmentItems: {
      type: Array as PropType<Array<AssessmentItem>>,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isSupervisor: {
      type: Boolean,
      default: false,
    },
    isEmployee: {
      type: Boolean,
      default: false,
    },
    finalScore: {
      type: Number,
      required: false,
    },
    testMode: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // const {} = usePlugin();
    const sections = computed(() =>
      groupBy((item) => {
        return item.section;
      }, localAssessmentItems.value)
    );
    const { isAdmin } = usePlugin();
    const sectionKeys = computed(() => Object.keys(sections.value));
    const localAssessmentItems = ref<AssessmentItem[]>([]);
    const localFinalScore = computed(() => {
      const rawScores = localAssessmentItems.value
        .filter(
          (x) =>
            (x.employee === true && props.isEmployee) ||
            (x.supervisor === true && props.isSupervisor) ||
            props.testMode
        )
        .map((x) => {
          if (x.finalScore !== true) return null;
          if (x.type === QuestionItemType.text) return null;
          if (
            x.type === QuestionItemType.scale ||
            x.type === QuestionItemType.scaleWithMore
          ) {
            return x.answer && x.answer.length > 0 ? parseInt(x.answer) / 5 : 0;
          }
          if (x.type === QuestionItemType.choice) {
            return parseInt(x.answer) === x.correctAnswer ? 1 : 0;
          }
          return 0;
        });
      const filtered = rawScores.filter((x) => x !== null) as number[];

      var total = filtered.length === 0 ? 0 : sum(filtered) / filtered.length;
      return Math.round(total * 100) / 100;
    });

    const isInvalid = (question: AssessmentItem) => {
      return (
        question.required &&
        !question.answer &&
        ((question.supervisor && props.isSupervisor) ||
          (question.employee && props.isEmployee) ||
          props.testMode)
      );
    };

    const isComplete = computed(() => {
      return (
        localAssessmentItems.value
          .map((x) => !isInvalid(x))
          .filter((x) => x === false).length === 0
      );
    });

    const onlyEmployee = computed(() => {
      return localAssessmentItems.value.findIndex((x) => x.supervisor) <= 0;
    });

    //In case load question item later.
    watch(
      () => props.questionItems,
      () => {
        if (
          props.assessmentItems == undefined ||
          props.assessmentItems.length === 0
        ) {
          localAssessmentItems.value = props.questionItems.map<AssessmentItem>(
            (x) => ({
              ...x,
              answer: "",
              more: "",
            })
          );
        }
      }
    );

    //In case load assesssment item later.
    watch(
      () => props.assessmentItems,
      () => {
        if (props.assessmentItems && props.assessmentItems.length > 0) {
          localAssessmentItems.value = props.assessmentItems;
        }
      }
    );

    //Auto emit output
    watch(
      () => localAssessmentItems.value,
      () => {
        emit("update:assessmentItems", localAssessmentItems.value);
        emit("update:finalScore", localFinalScore.value);
        emit("update:isComplete", isComplete.value);
        emit("update:onlyEmployee", onlyEmployee.value);
      },
      {
        deep: true,
      }
    );

    onMounted(() => {
      if (
        props.assessmentItems == undefined ||
        props.assessmentItems.length === 0
      ) {
        localAssessmentItems.value = props.questionItems.map<AssessmentItem>(
          (x) => ({
            ...x,
            answer: "",
            more: "",
          })
        );
      } else {
        localAssessmentItems.value = props.assessmentItems;
      }
    });

    return {
      sections,
      sectionKeys,
      QuestionItemType,
      localAssessmentItems,
      localFinalScore,
      isAdmin,
      isInvalid,
      isComplete,
    };
  },
});
